import styled from 'styled-components';
import { Divider } from 'legacyComponents/styled/lib';
import { media } from 'legacyComponents/styled/styleUtils';
import { customMedia, media as newMedia, pseudo } from 'styles/utils';
import { Avatar } from 'apps/components/shared';
import Button from 'components/Button';
import Spinner from 'components/Spinner';
import Icon from 'components/Icon';
import Heading from 'components/Heading';

// Be careful changing those values. They are used to make calculations, and to check if the dropdown should render down or up.
export const sizeConfig = {
  marginTop: 8,
  labelHeight: 28,
  inputFormMinHeight: 70,
  dropHeight: 220,
};

export const Main = styled.div`
  display: block;
  height: auto;
  margin: 0 auto;
  background-color: ${props => props.theme.color.wfWhite};
  box-shadow: ${props =>
    props.noBorder ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.15)'};
  padding: ${props => props.padding || '0px 40px 20px 40px'};
  width: 100%;
  /* Media queries */
  ${media.xs`
    padding: ${props => props.padding || '0 20px 20px 20px'};
  `}
  ${newMedia.large`
    height: ${props => props.isSideBarForm && '100%'};
  `}
`;

export const FormDivider = styled(Divider)`
  width: 100%;
`;

export const AutocompleteInputDivider = styled.div`
  width: calc(100% + 20px);
  margin: ${props =>
    props.lessMargin ? '2px 0px 12px -10px' : '12px 0px 12px -10px'};
  height: 1px;
  background-color: ${props => props.theme.color.wfGrey200};
`;

export const FormTitle = styled(Heading)`
  padding: ${props => props.theme.spacing.default} 0;
  color: ${props => props.formTitleColor || props.theme.color.primary};
  text-align: ${props => (props.centered ? 'center' : 'inherit')};
`;

export const HeadingStyled = styled(Heading)`
  color: ${props => props.formTitleColor};
  text-align: ${props => props.centered && 'center'};
  margin-bottom: ${props => props.isSideBarForm && props.theme.spacing.medium};
  margin-top ${props => props.isSideBarForm && props.theme.spacing.xlarge};
`;

const formStyles = `
  width: ${props => (props.responsive ? '100%' : '600px')};
  display: block;
  margin-right: auto;
  margin-left: auto;
  transition: opacity 300ms ease-in-out;
`;

export const FormWrapper = styled.form`
  ${formStyles};
  /* FIXME: Remove 600px units once form component can be refactored */
  width: ${props => (props.responsive ? '100%' : '600px')};
  max-width: ${props =>
    (props.isSideBarForm && props.responsive) || props.maxResponsive
      ? '600px'
      : null};
  opacity: ${props => (props.posting ? '0.4' : '1')};
  cursor: ${props =>
    props.posting ? 'progress !important' : 'inherit !important'};
  padding: ${props =>
    props.noPadding ? '0' : `${props.theme.spacing.default} 0 0 0`};
  ${media.s`
    width: 100%;
  `};
  margin-left: ${props => props.ml};
  margin-right: ${props => props.mr};
`;

export const FieldsWrapper = styled.div`
  ${customMedia.greaterThan('medium')`
    display: ${props => props.formColumns && 'grid'};
    grid-template-columns: ${props =>
      props.formColumns && `repeat(${props.formColumns}, 1fr)`};
    grid-gap: ${props => props.formColumns && props.theme.spacing.large};
  `};
`;

export const FormWrapperDiv = styled.div`
  ${formStyles};
  width: ${props => (props.responsive ? '100%' : '600px')};
  opacity: ${props => (props.posting ? '0.4' : '1')};
  cursor: ${props =>
    props.posting ? 'progress !important' : 'inherit !important'};
  padding: ${props =>
    props.noPadding ? '0' : `${props.theme.spacing.default} 0 0 0`};
  ${media.s`
    width: 100%;
  `};
`;

export const Label = styled.label`
  font-size: ${props => props.theme.font.size.default};
  font-weight: ${props =>
    props.bold
      ? props.theme.font.weight.xbold
      : props.theme.font.weight.regular};
  color: ${props => props.theme.color.wfBlack};
  line-height: ${sizeConfig.labelHeight}px;
`;

export const Optional = styled.span`
  color: ${props => props.theme.color.wfGrey400};
  font-weight: ${props => props.theme.font.weight.regular};
`;

export const FormField = styled.div`
  position: relative;
  align-self: end;
  grid-column: ${props => props.gridColumn && `1/${props.gridColumn}`};
  ${FormDivider} {
    margin-bottom: ${props => props.theme.spacing.default};
  }
  &:not(:first-of-type) {
    margin-top: ${props => !props.formColumns && '1.5rem'};
  }
  min-height: ${sizeConfig.inputFormMinHeight}px;
  ${customMedia.lessThan('medium')`
    margin-bottom: ${props => props.theme.space[4]};
    &:last-child {
      margin-bottom: auto;
    }
  `};
`;

export const InputWrapper = styled.div`
  /* looks like an input, but allows icon or text to be displayed within it */
  /* width: ${props => (props.responsive ? '100%' : '600px')}; */
  margin-top: ${sizeConfig.marginTop}px;
  color: ${props => props.theme.color.wfBlack};
  border: ${props =>
    props.focussed
      ? `1px solid ${props.theme.color.wfBlack}`
      : `1px solid ${props.theme.color.wfGrey500}`};
  border-radius: 0;
  padding: 12px 10px;
  position: relative;

  ${customMedia.lessThan('medium')`
    margin-top: 0;
  `}

  ${media.s`
    width: 100%;
  `};
  ${media.xs`
    padding: 10px;
  `};
`;

export const ItemsWrapper = styled(InputWrapper)`
  position: relative;
  padding: ${props =>
    props.hasItems && !props.focussed ? '12px 10px 2px 10px' : '12px 10px'};
`;

export const SuffixStyled = styled.span(({ theme }) => ({
  paddingLeft: theme.space[1],
  paddingRight: theme.space[1],
  top: 'auto',
  right: theme.space[3],
  position: 'absolute',
}));

export const WrappedInput = styled.input`
  width: ${props =>
    props.visible === false
      ? '1px'
      : props.responsive
      ? 'calc(100% - 20px)'
      : '100%'};
  font-weight: ${props =>
    props.variant === 'secondary'
      ? props.theme.font.weight.xbold
      : props.theme.font.weight.regular};
  color: ${props =>
    (props.visible === false && 'transparent') || props.theme.color.wfGrey900}
  /* border: 1px solid ${props => props.theme.color.wfGrey300}; */
  border: none;
  border-radius: 2px;
  padding: 0px;
  background-color: ${props =>
    props.variant === 'secondary'
      ? props.theme.color.wfGrey100
      : props.theme.color.wfWhite};
  ::placeholder {
    font-weight: ${props => props.theme.font.weight.regular};
    color: ${props => props.theme.color.wfGrey400};
    text-rendering: geometricPrecision;
  }
  &:focus {
    border-color: ${props =>
      props.variant === 'primary' && props.theme.color.wfGrey500};
  }
  &:hover {
    cursor: ${props => props.disabled && 'not-allowed'};
  }
`;

export const FormButton = styled(Button)`
  width: ${props => (props.removeFullWidth ? 'fit-content' : 'auto')};
  margin-top: ${props => props.marginTop || props.theme.spacing.large};
  margin-right: ${props => (props.marginRight ? props.marginRight : '0')};
  margin-left: ${props => (props.marginLeft ? props.marginLeft : '0')};
  cursor: ${props => (props.posting ? 'progress' : 'pointer')};
`;

export const CheckboxWrapper = styled(InputWrapper)`
  padding: 3px 0;
  border: 1px solid ${props => props.theme.color.wfWhite};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  /* display: inline-block; */
`;

const BULLET_SIZE = '16px';
const BULLET_PADDING = '2px';

export const Checkbox = styled.input`
  /* TODO: Recover theme colors */
  width: 40px;
  height: 20px;
  position: relative;
  float: right;
  -webkit-appearance: none;
  color: ${props => props.theme.color.wfGrey400};
  transition: color ease 0.2s;
  background-color: ${props => props.theme.color.wfWhite};
  border-radius: ${props => props.theme.borderRadius.large};
  cursor: pointer;
  overflow: hidden;
  &::before {
    ${pseudo('absolute', '100%', '100%')};
    top: 0;
    left: 0;
    background-color: currentColor;
  }
  &::after {
    ${pseudo('absolute', BULLET_SIZE, BULLET_SIZE)};
    top: 50%;
    left: ${BULLET_PADDING};
    transform: translateY(-50%);
    background: ${props => props.theme.color.wfWhite};
    border-radius: 50%;
    transition: left ease-out 0.2s;
  }
  &:checked {
    color: ${props => props.theme.color.primary};
    &::after {
      left: calc(100% - calc(${BULLET_SIZE} + ${BULLET_PADDING}));
    }
  }
`;

export const CheckboxText = styled.em`
  font-size: 0.95em;
  max-width: calc(100% - 55px);
  line-height: 1.4;
`;

export const InputValidationIcon = styled(Icon)`
  color: ${props =>
    props.valid ? props.theme.color.wfGreen : props.theme.color.wfOrange};
  opacity: ${props => (props.hidden ? '0' : '1')};
  transition: opacity 300ms ease-in-out;
  cursor: ${props => (props.valid ? 'auto' : 'pointer')};
  position: absolute;
  right: 10px;
  top: ${props => (props.positionFixed ? '14px' : '50%')};
  transform: ${props => !props.positionFixed && 'translateY(-50%)'};
  width: 14px;
  height: 14px;
`;

export const SpinnerStyled = styled(Spinner)`
  float: right;
  margin: -2px 0 0 0;
`;
// We may use it later for calculating OptionList top position
// top: ${props =>
//     props.dropUp
//       ? `calc(-${sizeConfig.dropHeight}px + ${sizeConfig.labelHeight +
//           sizeConfig.marginTop +
//           2}px)`
//       : '100%'};

export const OptionsList = styled.div`
  width: 100%;
  max-height: ${sizeConfig.dropHeight}px;
  overflow-y: auto;
  margin: -1px 0 0 0;
  color: rgba(0, 0, 0, 0.6);
  background-color: ${props => props.theme.color.wfWhite};
  font-size: 0.9375em;
  border: 1px solid ${props => props.theme.color.wfBlack};
  border-top-color: ${props =>
    props.dropUp ? props.theme.color.wfBlack : props.theme.color.wfGrey400};
  border-bottom-color: ${props =>
    props.dropUp ? props.theme.color.wfGrey400 : props.theme.color.wfBlack};
  position: absolute;
  // If we use react-tooltip inside of OptionList and it has fixed position
  // and any wrapper has transform, perspective, or filter properties, 
  // then we cannot use transform property because tooltip is positioned relative to this block
  // transform: translateY(${props => (props.dropUp ? '-100%' : '0%')});
  z-index: 5;

  ${customMedia.lessThan('medium')`
    top: ${props =>
      props.dropUp ? `${sizeConfig.labelHeight + 2}px` : '100%'};
  `}
`;

export const OptionWrapper = styled.div`
  margin: 0;
  color: ${props =>
    props.highlighted ? props.theme.color.wfBlue : props.theme.color.wfBlack};
  font-weight: ${props => props.theme.font.weight.regular};
  padding-left: ${props => (props.hasIndentation ? '10px' : '0')};
  font-size: 0.9375em;
  background-color: ${props =>
    props.highlighted
      ? props.highlightedColor
        ? props.highlightedColor
        : props.theme.color.wfBlue
      : props.highlightedToExpand
      ? props.theme.color.wfGrey100 // mouseover styling for expandable material categories
      : props.theme.color.wfWhite};
  border-bottom: 1px solid ${props => props.theme.color.wfGrey200};
  cursor: ${props => (props.heading ? '' : 'pointer')};
  border-radius: 1px;
  line-height: 1.4;
`;

export const Option = styled.div`
  padding-top: 10px;
  padding-bottom: 10px;
  padding-right: 10px;
  padding-left: ${props =>
    props.indentLevel ? `${props.indentLevel}0px` : '10px'};
  font-size: 0.9375em;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'cursor')};
  color: ${props =>
    props.highlighted
      ? props.theme.color.wfWhite
      : props.isSelectableForEntireCategory
      ? props.theme.color.wfGrey700
      : props.disabled
      ? props.theme.color.wfGrey500
      : props.heading
      ? props.theme.color.wfGrey
      : props.theme.color.wfBlack};
  font-weight: ${props =>
    (props.isExpandable && props.expanded) ||
    props.isAutoExpandedCategory ||
    props.heading
      ? props.theme.font.weight.xbold
      : props.theme.font.weight.regular};
  font-style: ${props =>
    props.isSelectableForEntireCategory ? 'italic' : 'default'};
`;

export const OptionCount = styled.span`
  color: ${props => props.theme.color.wfGrey600};
  font-weight: ${props => props.theme.font.weight.regular};
`;

export const Selected = styled.span`
  color: ${props =>
    props.color ? `${props.color}` : props.theme.color.wfGrey900};
`;

export const Placeholder = styled.span`
  color: ${props => props.theme.color.wfGrey400};
  ${media.xs`
    font-size: 16px;
  `};
`;

export const OwnerAvatar = styled(Avatar)`
  float: left;
  margin-top: ${props => (props.noMarginTop ? '0' : '9px')};
  margin-right: 10px;
  margin-left: 10px;
`;

export const CreateNew = styled.span`
  position: absolute;
  bottom: 15px;
  right: 10px;
  font-size: 10px;
  color: ${props => props.theme.color.primary};
`;

export const FormEmoji = styled.p`
  height: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-size: 3em;
  ${media.xs`
    font-size: 2.5em;
  `};
`;

export const FormButtonWrapper = styled.div`
  display: flex;
  justify-content: ${props => (props.isSideBarForm ? 'start' : 'center')};
`;

export const EmptyWrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: ${sizeConfig.inputFormMinHeight}px;

  ${customMedia.lessThan('medium')`
    min-height: 0px;
  `}
`;

export const EmptyLabelWrapper = styled.label`
  display: flex;
  width: 100%;
  min-height: 24px;

  ${customMedia.lessThan('medium')`
    min-height: 0px;
  `}
`;

export const Arrow = styled(Icon)`
  float: right;
  color: rgba(0, 0, 0, 0.3);
  margin-right: ${props => (props.shiftLeft ? '25px' : '0')};
`;

export const ColorArrow = styled(Arrow)`
  color: ${props =>
    props.expanded ? props.theme.color.wfOrange : props.theme.color.wfBlue};
`;

export const ResetIcon = styled(Icon)`
  vertical-align: bottom;
  color: ${props => props.theme.color.wfOrange};
  margin-left: ${props => props.theme.spacing.xsmall};
  opacity: 0;
`;
